import { useQuery } from "@tanstack/react-query";
import HttpService from "../../hooks/Https-services";
import { APIRoutes } from "../../utils/constants";

export interface GiftOption {
  tier?: string;
  billing?: string;
  priceInCurrency?: string | number;
  currency?: string;
  [key: string]: any;
}

export interface GiftOptionsData {
  options: GiftOption[];
  termMax: number;
}

/**
 * Loads the gift-subscription tier/price options, cached via react-query (the
 * QueryClient provided by V2Shell). Revisiting /buy-gift within the stale
 * window serves the cached prices instead of refetching on every mount, the
 * same way the plans page loads its data. An empty options payload is treated
 * as an error so the wizard shows its pricing-error retry affordance.
 */
export function useGiftOptions() {
  return useQuery<GiftOptionsData>({
    queryKey: ["v2", "gift", "options"],
    // Tiers/prices/term cap change rarely, so cache longer than the 30s v2
    // default to avoid a fetch on every gift-page visit.
    staleTime: 5 * 60 * 1000,
    retry: 2,
    queryFn: async ({ signal }) => {
      const res = await HttpService.get(
        APIRoutes.GiftSubscriptionOptions,
        signal,
      );
      const data = res?.data?.data;
      const options: GiftOption[] = Array.isArray(data?.options)
        ? data.options
        : [];
      if (!options.length) throw new Error("No gift options available");
      const parsedMax = Number(data?.termMax);
      const termMax = !isNaN(parsedMax) && parsedMax > 0 ? parsedMax : 24;
      return { options, termMax };
    },
  });
}
